

import React, { useState, useEffect, lazy, Suspense } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Box, Tab, Tabs, Typography, Container, Alert, CircularProgress, Card, CardContent, Chip, Avatar, Stack, Button } from '@mui/material';
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./Login.css";
import GoogleLoginSection from "./components/Auth/GoogleLoginSection";
import OtpLogin from "./components/Auth/OtpLogin";
import { useTheme } from "@mui/material";
import { motion } from 'framer-motion';
import { Security, CheckCircle, ArrowBack } from '@mui/icons-material';

const ReCaptcha = lazy(() => import("./components/Auth/ReCaptcha"));

const Login = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [tabValue, setTabValue] = useState(0);
  const [isAuthReady, setIsAuthReady] = useState(false);
  const [isVerified, setIsVerified] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("jwtToken");
    if (token) {
      const redirectPath = sessionStorage.getItem("redirect") || "/dashboard";
      navigate(redirectPath, { replace: true });
    } else {
      setIsAuthReady(true);
    }
  }, [navigate]);

  const handleTabChange = (event, newValue) => {
    if (!isVerified) {
      toast.error("Please verify the captcha first before selecting a login method.");
      return;
    }
    setTabValue(newValue);
  };

  const handleReCaptchaVerify = () => {
    setIsVerified(true);
    toast.success("Captcha verified! You can now choose a login method.");
  };

  if (!isAuthReady) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: theme.palette.background.default,
        }}
      >
        <Typography>Initializing login...</Typography>
      </Box>
    );
  }

  return (
    <Box
      className="login-container"
      sx={{
        minHeight: "100vh",
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        position: 'relative',
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        py: 4,
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: 'url(https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1920&h=1080&fit=crop)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          opacity: 0.1,
        },
      }}
    >
      <ToastContainer />
      
      {/* Back to Landing Button */}
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate('/')}
        sx={{
          position: 'absolute',
          top: 20,
          left: 20,
          color: 'white',
          bgcolor: 'rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(10px)',
          borderRadius: '24px',
          px: 3,
          py: 1,
          fontWeight: 600,
          '&:hover': {
            bgcolor: 'rgba(255, 255, 255, 0.2)',
            transform: 'translateY(-2px)',
          },
          transition: 'all 0.3s',
        }}
      >
        Back to Home
      </Button>

      <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1 }}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Avatar
                sx={{
                  width: 80,
                  height: 80,
                  bgcolor: 'white',
                  margin: '0 auto 20px',
                  fontSize: '2rem',
                  fontWeight: 700,
                  color: '#667eea',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
                }}
              >
                A
              </Avatar>
            </motion.div>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                color: 'white',
                mb: 1,
                textShadow: '2px 2px 8px rgba(0,0,0,0.2)',
              }}
            >
              AssetRack
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: 'rgba(255, 255, 255, 0.9)',
                fontWeight: 400,
              }}
            >
              Smart Inventory Management System
            </Typography>
            <Chip
              label="Secure Login"
              icon={<Security sx={{ fontSize: 16 }} />}
              sx={{
                mt: 2,
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                color: 'white',
                backdropFilter: 'blur(10px)',
                fontWeight: 600,
              }}
            />
          </Box>

          {/* Login Card */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <Card
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(20px)',
                borderRadius: '32px',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                boxShadow: '0 24px 64px rgba(0, 0, 0, 0.15)',
                overflow: 'hidden',
              }}
            >
              {/* reCAPTCHA Section */}
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ textAlign: 'center', mb: 4 }}>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      mb: 1,
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }}
                  >
                    Security Verification
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: '#666', mb: 3 }}
                  >
                    Please verify you're human before choosing a login method
                  </Typography>
                  
                  {!isVerified && (
                    <Alert
                      severity="info"
                      sx={{
                        mb: 3,
                        borderRadius: '12px',
                        '& .MuiAlert-message': { fontWeight: 500 },
                      }}
                    >
                      Complete the captcha below to unlock login options
                    </Alert>
                  )}
                  
                  {isVerified && (
                    <Alert
                      severity="success"
                      sx={{
                        mb: 3,
                        borderRadius: '12px',
                        '& .MuiAlert-message': { fontWeight: 500 },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckCircle sx={{ fontSize: 20 }} />
                        Verified! You can now choose your preferred login method
                      </Box>
                    </Alert>
                  )}

                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Suspense fallback={<CircularProgress />}>
                      <ReCaptcha onVerify={handleReCaptchaVerify} theme="light" />
                    </Suspense>
                  </Box>
                </Box>

                {/* Login Method Tabs */}
                <Box sx={{ 
                  opacity: isVerified ? 1 : 0.5, 
                  pointerEvents: isVerified ? 'auto' : 'none',
                  transition: 'opacity 0.3s ease',
                }}>
                  <Tabs
                    value={tabValue}
                    onChange={handleTabChange}
                    variant="fullWidth"
                    sx={{
                      mb: 3,
                      '& .MuiTabs-indicator': {
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        height: 3,
                        borderRadius: '3px',
                      },
                      '& .MuiTab-root': {
                        py: 2,
                        fontSize: '1rem',
                        fontWeight: 600,
                        color: '#666',
                        '&.Mui-selected': {
                          color: '#667eea',
                        },
                      },
                    }}
                  >
                    <Tab label="Google Login" />
                    <Tab label="Email OTP" />
                  </Tabs>

                  <Box sx={{ minHeight: 200 }}>
                    {tabValue === 0 && <GoogleLoginSection />}
                    {tabValue === 1 && <OtpLogin />}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </motion.div>

          {/* Footer */}
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Typography
              variant="caption"
              sx={{
                color: 'rgba(255, 255, 255, 0.8)',
                fontWeight: 500,
              }}
            >
              🔒 Secure login powered by Firebase & Advanced Encryption
            </Typography>
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default Login;
